import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './eventcalendar.css';
import EventCard from '../../Components/EventCard/EventCard';
import EventDetailModal from '../../Components/EventDetailModal/EventDetailModal';

const EventCalendar = ({ events }) => {
  const navigate = useNavigate();
  const [view, setView] = useState('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedDayEvents, setSelectedDayEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const calendarRef = useRef(null);

  const today = new Date();
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const startDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  
  // 월요일부터 시작하도록 조정 (0=일요일, 1=월요일)
  const adjustedStartDay = startDay === 0 ? 6 : startDay - 1;

  const days = [];
  const prevMonthDays = new Date(year, month, 0).getDate();

  // 이전 달의 빈 공간 채우기 (날짜 표시 안 함)
  for (let i = adjustedStartDay; i > 0; i--) {
    days.push({ day: '', isCurrentMonth: false });
  }

  // 현재 달의 날짜 채우기
  for (let i = 1; i <= daysInMonth; i++) {
    days.push({ day: i, isCurrentMonth: true, date: new Date(year, month, i) });
  }

  // 다음 달의 빈 공간 채우기 (날짜 표시 안 함)
  const nextMonthDays = 42 - days.length; // 6주 * 7일 = 42
  for (let i = 1; i <= nextMonthDays; i++) {
    days.push({ day: '', isCurrentMonth: false });
  }

  const eventsByDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => e.date === dateStr);
  };

  const getMonthName = (month) => {
    const months = [
      '1월', '2월', '3월', '4월', '5월', '6월',
      '7월', '8월', '9월', '10월', '11월', '12월'
    ];
    return months[month];
  };

  const getWeekdayName = (day) => {
    const weekdays = ['월', '화', '수', '목', '금', '토', '일'];
    return weekdays[day];
  };

  const getWeekNumber = (date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    const dayOfWeek = firstDay.getDay();
    const diff = date.getDate() + dayOfWeek - 1;
    return Math.ceil(diff / 7);
  };

  const getEventsByWeek = () => {
    const weeks = {};
    // 현재 월의 이벤트만 필터링
    const currentMonthEvents = events.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate.getMonth() === month && eventDate.getFullYear() === year;
    });

    currentMonthEvents.forEach(event => {
      const eventDate = new Date(event.date);
      const weekNumber = getWeekNumber(eventDate);
      if (!weeks[weekNumber]) {
        weeks[weekNumber] = [];
      }
      weeks[weekNumber].push(event);
    });
    return weeks;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedEvent(null);
  };

  const changeMonth = (delta) => {
    setCurrentDate(new Date(year, month + delta, 1));
  };

  const isToday = (day) => {
    return day === today.getDate() && 
           month === today.getMonth() && 
           year === today.getFullYear();
  };

  const handleDayClick = (dayObj) => {
    if (view !== 'monthly' || !dayObj.isCurrentMonth) return;

    if (selectedDay === dayObj.day) {
      setSelectedDay(null);
      setSelectedDayEvents([]);
    } else {
      const dayEvents = eventsByDay(dayObj.day);
      setSelectedDay(dayObj.day);
      setSelectedDayEvents(dayEvents);
    }
  };

  // 여러 장르를 처리하기 위한 유틸리티 함수들
  const parseGenres = (genreString) => {
    if (!genreString) return [];
    return genreString.split(',').map(genre => genre.trim().toLowerCase()).filter(genre => genre);
  };

  const getPrimaryGenre = (genreString) => {
    const genres = parseGenres(genreString);
    return genres.length > 0 ? genres[0] : '';
  };

  const getValidGenres = (genreString) => {
    const validGenres = ['edm', 'hiphop', 'techno', 'house'];
    const genres = parseGenres(genreString);
    return genres.filter(genre => validGenres.includes(genre));
  };

  // 장르별 색상 매핑
  const getGenreColor = (genre) => {
    const colorMap = {
      'edm': '#22c55e',
      'hiphop': '#eab308', 
      'techno': '#3b82f6',
      'house': '#ec4899'
    };
    return colorMap[genre] || '#666';
  };

  // 다중 장르를 위한 스타일 생성
  const createMultiGenreStyle = (genres) => {
    if (genres.length === 0) return { backgroundColor: '#666' };
    if (genres.length === 1) return { backgroundColor: getGenreColor(genres[0]) };
    
    // 2개 장르인 경우 linear-gradient로 정확히 반반 분할
    if (genres.length === 2) {
      return {
        background: `linear-gradient(90deg, ${getGenreColor(genres[0])} 50%, ${getGenreColor(genres[1])} 50%)`
      };
    }
    
    // 3개 이상인 경우 conic-gradient 사용
    const sectorSize = 360 / genres.length;
    const gradientStops = [];
    
    genres.forEach((genre, index) => {
      const color = getGenreColor(genre);
      const startAngle = index * sectorSize;
      const endAngle = (index + 1) * sectorSize;
      
      gradientStops.push(`${color} ${startAngle}deg ${endAngle}deg`);
    });
    
    return {
      background: `conic-gradient(${gradientStops.join(', ')})`
    };
  };



  return (
    <div className="event-calendar-container" ref={calendarRef}>
      <div className="calendar-header">
        <button onClick={() => changeMonth(-1)} aria-label="Previous month">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <h2>{getMonthName(month)} {year}</h2>
        <button onClick={() => changeMonth(1)} aria-label="Next month">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>

      <div className="view-toggle">
        <div className="view-buttons">
          <button
            className={view === 'week' ? 'active' : ''}
            onClick={() => setView('week')}
          >
            주간
          </button>
          <button
            className={view === 'monthly' ? 'active' : ''}
            onClick={() => setView('monthly')}
          >
            월간
          </button>
        </div>
        
        <div className="genre-legend">
          <div className="legend-item">
            <div className="legend-dot edm"></div>
            <span>EDM</span>
          </div>
          <div className="legend-item">
            <div className="legend-dot hiphop"></div>
            <span>힙합</span>
          </div>
          <div className="legend-item">
            <div className="legend-dot techno"></div>
            <span>테크노</span>
          </div>
          <div className="legend-item">
            <div className="legend-dot house"></div>
            <span>하우스</span>
          </div>
        </div>
      </div>

      {view === 'monthly' && (
        <div className="monthly-view">
          <div className="weekdays">
            {['월', '화', '수', '목', '금', '토', '일'].map((d) => (
              <div key={d} className="weekday">{d}</div>
            ))}
          </div>
          <div className="days">
            {days.map((dayObj, index) => {
              const dayEvents = dayObj.isCurrentMonth ? eventsByDay(dayObj.day) : [];
              
              // 백그라운드 스타일 결정 로직
              let backgroundStyle = {};
              let additionalClass = '';
              
              if (dayEvents.length > 0) {
                const firstEvent = dayEvents[0];
                
                if (firstEvent.poster_image_url && firstEvent.poster_image_url.trim() !== '') {
                  // 포스터 이미지가 있는 경우
                  backgroundStyle = { 
                    backgroundImage: `url("${firstEvent.poster_image_url}")`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  };
                  additionalClass = 'has-event-poster';
                } else {
                  // 포스터 이미지가 없는 경우 장르별 그라데이션 배경 적용
                  const validGenres = getValidGenres(firstEvent.genre);
                  if (validGenres.length > 0) {
                    backgroundStyle = createMultiGenreStyle(validGenres);
                    additionalClass = 'has-genre-background';
                  }
                }
              }
              
              return (
                <div
                  key={`day-${index}-${dayObj.day}`}
                  className={`day ${!dayObj.isCurrentMonth ? 'other-month' : ''} ${dayObj.isCurrentMonth && isToday(dayObj.day) ? 'today' : ''} ${selectedDay === dayObj.day ? 'active' : ''} ${additionalClass}`}
                  onClick={() => handleDayClick(dayObj)}
                  style={backgroundStyle}
                >
                  <div className="day-number">{dayObj.day}</div>
                  {dayEvents.length > 0 && (
                    <div className="event-dots-container">
                      {dayEvents.slice(0, 4).map(event => {
                        const validGenres = getValidGenres(event.genre);
                        const dotStyle = createMultiGenreStyle(validGenres);
                        return (
                          <div 
                            key={event.id}
                            className="event-dot multi-genre"
                            style={dotStyle}
                            title={`${event.title} - ${validGenres.join(', ').toUpperCase()}`}
                          ></div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Selected Day's Events - 월간 뷰일 때만 렌더링 */}
      {view === 'monthly' && (
        <>
          <div className={`selected-events-container ${selectedDay ? 'visible' : ''}`}>
            {selectedDay && (
              selectedDayEvents.length > 0 ? (
                <>
                  <h3 className="selected-events-header">{month + 1}월 {selectedDay}일 이벤트</h3>
                  <div className="selected-event-list">
                    {selectedDayEvents.map(event => (
                      <div key={event.id} className={`selected-event-item ${getPrimaryGenre(event.genre)}`} onClick={() => handleEventClick(event)}>
                        {event.poster_image_url && <img src={event.poster_image_url} alt={event.title} className="event-poster-thumbnail" />}
                        <div className="event-details">
                          <div className="event-title-wrapper">
                            <span className="event-title">{event.title}</span>
                            <div className="event-genre-tags">
                              {getValidGenres(event.genre).map((genre, index) => (
                                <span key={`${event.id}-genre-${index}`} className={`event-genre-tag genre-${genre}`}>
                                  {genre.toUpperCase()}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div className="event-location">📍 {event.location.toLowerCase()}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="no-events-for-day">
                  <p>해당 날짜에는 예정된 이벤트가 없습니다.</p>
                </div>
              )
            )}
          </div>
          
          {!selectedDay && (
            <div className="events-placeholder">
              <p>날짜를 선택하여 이벤트 일정을 확인하세요!</p>
            </div>
          )}
        </>
      )}

      {view === 'week' && (
        <div className="weekly-view">
          {Object.keys(getEventsByWeek()).length === 0 ? (
            <div className="no-events-message">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              <h3>예정된 이벤트가 없습니다</h3>
              <p>이번 달에는 예정된 이벤트가 없습니다.</p>
            </div>
          ) : (
            Object.entries(getEventsByWeek()).map(([week, weekEvents]) => (
              <div key={week} className="week-group">
                <h4 className="week-title">{week}주차</h4>
                <div className="event-card-list">
                  {weekEvents.map(event => (
                    <EventCard key={event.id} event={event} onEventClick={handleEventClick} />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Event Detail Modal */}
      <EventDetailModal 
        event={selectedEvent}
        isOpen={isModalOpen}
        onClose={handleModalClose}
      />
    </div>
  );
};

export default EventCalendar;
